<template>
  <section
    v-if="loading || error || trials.length"
    class="trial-history card"
    aria-label="등록 전 체험 이력"
  >
    <div class="history-head">
      <div>
        <h2 class="history-title">등록 전 체험 이력</h2>
        <p class="history-help">
          체험 고객 관리에 남긴 원본 기록입니다. 회원 메모로 복제하지 않습니다.
        </p>
      </div>
      <span v-if="!loading && !error" class="history-count"
        >{{ trials.length }}건</span
      >
    </div>

    <p v-if="loading" class="history-empty">불러오는 중...</p>
    <div v-else-if="error" class="history-empty history-error">
      <span>체험 이력을 불러오지 못했습니다.</span>
      <button type="button" @click="load">다시 시도</button>
    </div>

    <div v-else class="history-list">
      <article
        v-for="trial in trials"
        :key="trial.request.id"
        class="history-item"
      >
        <div class="history-summary">
          <div>
            <strong>{{ dateTime(trial.request.created_at) }}</strong>
            <span>{{ visitLabel(trial.visit) }}</span>
          </div>
          <router-link
            :to="{
              path: '/free-trial-requests',
              query: { request_id: trial.request.id },
            }"
          >
            원본 보기 →
          </router-link>
        </div>

        <dl class="history-fields">
          <div>
            <dt>담당</dt>
            <dd>{{ trial.profile.assigned_coach_name || "미지정" }}</dd>
          </div>
          <div>
            <dt>상담 상태</dt>
            <dd>
              {{ enumLabel("consultation", trial.profile.consultation_status) }}
            </dd>
          </div>
          <div>
            <dt>운동 목적</dt>
            <dd>{{ labels("goal", trial.profile.goals) }}</dd>
          </div>
          <div>
            <dt>등록 고민</dt>
            <dd>{{ labels("objection", trial.profile.objection_reasons) }}</dd>
          </div>
          <div v-if="trial.profile.customer_comment">
            <dt>고객 이야기</dt>
            <dd>{{ trial.profile.customer_comment }}</dd>
          </div>
          <div v-if="trial.profile.coach_note">
            <dt>코치 관찰</dt>
            <dd>{{ trial.profile.coach_note }}</dd>
          </div>
          <div v-if="trial.profile.next_action">
            <dt>다음 행동</dt>
            <dd>{{ trial.profile.next_action }}</dd>
          </div>
        </dl>

        <div v-if="trial.contacts.length" class="contact-list">
          <h3>상담·연락 기록</h3>
          <div
            v-for="contact in trial.contacts"
            :key="contact.id"
            class="contact-item"
          >
            <div class="contact-meta">
              <strong>{{ contact.author_name || "작성자 미확인" }}</strong>
              <span>{{ dateTime(contact.created_at) }}</span>
              <span>{{ enumLabel("channel", contact.channel) }}</span>
              <span>{{ enumLabel("result", contact.result) }}</span>
            </div>
            <p v-if="contact.note">{{ contact.note }}</p>
            <p v-if="contact.next_follow_up_at" class="follow-up">
              다음 연락 {{ dateTime(contact.next_follow_up_at) }}
            </p>
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
import { apiJson } from "@/lib/api";

const LABELS = {
  consultation: {
    not_started: "상담 전",
    considering: "고민 중",
    follow_up: "후속 연락 중",
    on_hold: "보류",
    closed: "종료",
  },
  goal: {
    fitness: "체력 향상",
    weight_loss: "감량",
    habit: "운동 습관",
    performance: "운동 능력",
    health: "건강 관리",
    other: "기타",
  },
  objection: {
    cost: "비용",
    schedule: "일정",
    distance: "거리",
    intensity: "운동 강도",
    effect_confidence: "효과 확신",
    sustainability: "꾸준함",
    facility: "시설",
    other: "기타",
    unknown: "미확인",
  },
  channel: {
    in_person: "현장",
    phone: "전화",
    sms: "문자",
    naver: "네이버",
    other: "기타",
  },
  result: {
    reached: "연락 완료",
    no_answer: "부재",
    message_sent: "메시지 발송",
    follow_up_set: "재연락 예정",
    closed: "종료",
  },
};

export default {
  name: "MemberTrialHistory",
  props: {
    userId: { type: String, required: true },
  },
  data() {
    return {
      trials: [],
      loading: false,
      error: false,
      requestSeq: 0,
    };
  },
  created() {
    this.load();
  },
  watch: {
    userId() {
      this.trials = [];
      this.error = false;
      this.load();
    },
  },
  methods: {
    async load() {
      const userId = this.userId;
      const seq = ++this.requestSeq;
      this.loading = true;
      this.error = false;
      try {
        const data = await apiJson(
          `/v1/admin/members/${encodeURIComponent(userId)}/trial-history`
        );
        if (seq !== this.requestSeq || userId !== this.userId) return;
        this.trials = Array.isArray(data.trials)
          ? data.trials.map((trial) => ({
              ...trial,
              request: trial.request || {},
              profile: trial.profile || {},
              visit: trial.visit || {},
              registration: trial.registration || {},
              contacts: Array.isArray(trial.contacts) ? trial.contacts : [],
            }))
          : [];
      } catch (error) {
        if (seq !== this.requestSeq || userId !== this.userId) return;
        console.error("체험 이력 로드 실패:", error);
        this.trials = [];
        this.error = true;
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },
    dateTime(value) {
      if (!value) return "날짜 미확인";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return new Intl.DateTimeFormat("ko-KR", {
        timeZone: "Asia/Seoul",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(date);
    },
    enumLabel(group, value) {
      return LABELS[group]?.[value] || value || "미확인";
    },
    labels(group, values) {
      return Array.isArray(values) && values.length
        ? values.map((value) => this.enumLabel(group, value)).join(", ")
        : "미확인";
    },
    visitLabel(visit) {
      if (!visit || visit.status === "unknown") return "방문 미확인";
      const state = {
        booked: "예약",
        attended: "체험 참석",
        cancelled: "예약 취소",
      }[visit.status];
      const schedule = [visit.class_date, visit.start_time]
        .filter(Boolean)
        .join(" ");
      return [schedule, state || visit.status].filter(Boolean).join(" · ");
    },
  },
};
</script>

<style scoped>
.trial-history {
  background: #151718;
  border-radius: 10px;
  padding: 20px 24px;
  box-sizing: border-box;
  min-width: 0;
}
.history-head,
.history-summary,
.contact-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.history-title,
.contact-list h3 {
  margin: 0;
  color: #fff;
}
.history-title {
  font-size: 18px;
}
.history-help,
.history-empty {
  margin: 5px 0 0;
  color: #8e8e8e;
  font-size: 13px;
}
.history-count {
  padding: 2px 9px;
  border-radius: 999px;
  background: #0a0a0a;
  color: #8e8e8e;
  font-size: 12px;
}
.history-list {
  display: grid;
  gap: 10px;
  margin-top: 16px;
}
.history-item {
  padding: 14px;
  border-radius: 8px;
  background: #0a0a0a;
  border: 1px solid #292b2d;
}
.history-summary > div {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.history-summary strong {
  color: #fff;
  font-size: 14px;
}
.history-summary span,
.contact-meta span {
  color: #8e8e8e;
  font-size: 12px;
}
.history-summary a,
.history-error button {
  color: #78ff47;
  background: transparent;
  border: 0;
  text-decoration: none;
  cursor: pointer;
}
.history-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 16px;
  margin: 14px 0 0;
}
.history-fields > div {
  display: grid;
  grid-template-columns: 70px minmax(0, 1fr);
  gap: 8px;
}
.history-fields dt,
.history-fields dd {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
}
.history-fields dt {
  color: #6b7075;
}
.history-fields dd {
  color: #d8d8d8;
  overflow-wrap: anywhere;
}
.contact-list {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #292b2d;
}
.contact-list h3 {
  font-size: 13px;
}
.contact-item {
  margin-top: 9px;
}
.contact-meta {
  justify-content: flex-start;
}
.contact-meta strong {
  color: #d8d8d8;
  font-size: 12px;
}
.contact-item p {
  margin: 5px 0 0;
  color: #fff;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.contact-item .follow-up {
  color: #78ff47;
  font-size: 12px;
}
.history-error {
  display: flex;
  align-items: center;
  gap: 8px;
}
@media (max-width: 700px) {
  .history-fields {
    grid-template-columns: 1fr;
  }
}
</style>
